// 前端版本自动更新：通过对比构建 hash，在路由切换时静默刷新以加载最新资源

import type { Router } from "vue-router";

/** reload 前暂存的目标路由，刷新后由 restorePendingRoute 恢复 */
const PENDING_ROUTE_KEY = "__app_pending_route__";
/** 标记正在刷新，防止重复触发 reload 导致死循环 */
const RELOADING_KEY = "__app_update_reloading__";

/** 懒加载 chunk 被服务端清理后，动态 import 失败的常见错误文案 */
const CHUNK_ERROR_PATTERNS = [/Failed to fetch dynamically imported module/i, /Loading chunk [\d]+ failed/i, /Unable to preload CSS/i];

// 0.1> 内部工具 -----------------------------------------------------------------------------------------------------

/** 拼接 version.json 请求地址（兼容 VITE base 路径） */
function getVersionUrl(): string {
    const base = import.meta.env.BASE_URL || "/";
    const normalizedBase = base.endsWith("/") ? base : `${base}/`;
    return `${normalizedBase}version.json`;
}

// 1.0> Hash 读取 ----------------------------------------------------------------------------------------------------

/** 从 index.html 注入的 meta 标签读取当前会话的构建 hash */
export function getLocalHash(): string | null {
    const meta = document.querySelector('meta[name="app-hash"]');
    return meta?.getAttribute("content") || null;
}

/** 拉取服务端最新 hash，禁用缓存以确保拿到最新版本 */
export async function fetchRemoteHash(): Promise<string | null> {
    try {
        console.log("meta:", getVersionUrl());
        const url = `${getVersionUrl()}?_t=${Date.now()}`;
        const res = await fetch(url, { cache: "no-store" });
        console.log("meta:", res);
        if (!res.ok) return null;
        const data = (await res.json()) as { hash?: string };
        console.log("meta:", data, import.meta.env.PROD);
        return data.hash || null;
    } catch (e) {
        console.error("meta error:", e);
        return null;
    }
}

// 2.0> 刷新与路由恢复 -----------------------------------------------------------------------------------------------

/** 触发页面刷新；可选保存目标路由，供 reload 后跳回 */
export function reloadForUpdate(pendingRoute?: string): void {
    if (sessionStorage.getItem(RELOADING_KEY)) return;

    sessionStorage.setItem(RELOADING_KEY, "1");
    if (pendingRoute) {
        sessionStorage.setItem(PENDING_ROUTE_KEY, pendingRoute);
    }
    window.location.reload();
}

/**
 * 路由切换时检测版本：本地 hash 与服务端不一致则刷新页面
 * @returns true 表示已触发 reload，调用方应中止后续导航
 */
export async function checkOnRouteChange(toFullPath: string): Promise<boolean> {
    try {
        console.log("meta:", import.meta.env.PROD);
        if (!import.meta.env.PROD) return false;

        // 刚完成 reload，清除标记并放行首次导航
        console.log("meta:", sessionStorage.getItem(RELOADING_KEY));
        if (sessionStorage.getItem(RELOADING_KEY)) {
            sessionStorage.removeItem(RELOADING_KEY);
            return false;
        }

        const localHash = getLocalHash();
        console.log("meta:", localHash);
        if (!localHash) return false;

        const remoteHash = await fetchRemoteHash();
        console.log("meta:", remoteHash);
        if (!remoteHash || remoteHash === localHash) return false;
    } catch (e) {
        console.error("meta error:", e);
        return false;
    }

    reloadForUpdate(toFullPath);
    return true;
}

/** 页面 reload 后，恢复刷新前用户想要访问的路由 */
export function restorePendingRoute(router: Router): void {
    console.log("meta:", import.meta.env.PROD);
    if (!import.meta.env.PROD) return;

    const pendingRoute = sessionStorage.getItem(PENDING_ROUTE_KEY);
    if (!pendingRoute) return;

    sessionStorage.removeItem(PENDING_ROUTE_KEY);
    router.replace(pendingRoute).catch(() => {});
}

// 3.0> Chunk 加载失败兜底 -------------------------------------------------------------------------------------------

/** 判断是否为旧版本缓存导致的懒加载 chunk 404 */
export function isChunkLoadError(err: unknown): boolean {
    const message = err instanceof Error ? err.message : String(err);
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export default {
    getLocalHash,
    fetchRemoteHash,
    checkOnRouteChange,
    restorePendingRoute,
    isChunkLoadError,
    reloadForUpdate,
};
